import type {
  IReportColumn,
  IReportGroupHeaderField,
  IReportRequest,
  IReportResult,
  IReportGrouping,
  IReportRow,
} from "../../../../models/report.dto";
import { ReportPeriodicity } from "../../../../models/report.dto";
import { endOfMonth, startOfMonth, subMonths } from "date-fns";
import {
  getCrmMessagesByEstablishmentMonthly,
  getDataBusRequestsByEstablishment,
  getDigitalSignaturesByEstablishmentMonthly,
  getMostAccessedTicketServiceAreasMonthly,
  getTicketsByAutomationFailure,
  getTicketsByAutomationInteraction,
  getTicketsByChannelAndUserDaily,
  getTicketsByChannelAndUserMonthly,
  getTicketsByClosureReason,
} from "../../../../services/report.service";
import { EReportCode } from "./reportConstants";
import { normalizeEmptyReportRows } from "./reportExecutionEmptyState";

export interface IReportTableRow extends IReportRow {
  id: string;
}

export interface IReportExecutionSearchParams extends IReportRequest {
  reportCode?: EReportCode;
  reportName?: string;
  dateIntervalType?: TReportDateIntervalType;
  monthPeriodType?: TReportMonthPeriodType;
  selectedMonthDate?: string;
}

export interface IReportResultSearchParams extends IReportExecutionSearchParams {
  workspaceId?: string[];
  ticketWorkspaceIds?: string[];
  workspaceNames?: string[];
  ticketServiceNames?: string[];
  ticketChannelNames?: string[];
  userNames?: string[];
  automationCode?: string[];
  automationCodeExcept?: string[];
  establishmentId?: string[];
  esignRuleId?: string[];
  automationCodeNames?: string[];
  automationCodeExceptNames?: string[];
  establishmentNames?: string[];
  esignRuleNames?: string[];
}

export interface IReportFilterSummaryItem {
  label: string;
  value: string;
  values?: string[];
}

export type TReportDateIntervalType =
  | "today"
  | "this_week"
  | "this_month"
  | "last_month"
  | "last_30_days"
  | "last_6_months"
  | "select_period";

export type TReportMonthPeriodType =
  | "this_month"
  | "last_month"
  | "select_month";

export const REPORT_EXECUTION_TRANSLATION_PATH = "page.reports.execution.";
export const REPORT_DATE_INTERVAL_TYPE_QUERY_PARAM = "dateIntervalType";
export const REPORT_MONTH_PERIOD_TYPE_QUERY_PARAM = "monthPeriodType";
export const REPORT_SELECTED_MONTH_DATE_QUERY_PARAM = "selectedMonthDate";
export const REPORT_DATE_INTERVAL_TYPES: TReportDateIntervalType[] = [
  "today",
  "this_week",
  "this_month",
  "last_month",
  "last_30_days",
  "last_6_months",
  "select_period",
];
export const REPORT_MONTH_PERIOD_TYPES: TReportMonthPeriodType[] = [
  "this_month",
  "last_month",
  "select_month",
];

export const translateReportLabel = (
  label: string | null | undefined,
  t: (key: string, defaultValue?: string) => string,
) => {
  if (!label) return "";

  const translated = t(label);

  if (translated !== label) {
    return translated;
  }

  const tableScopedLabel = t(`page.reports.execution.table.${label}`);

  return tableScopedLabel === `page.reports.execution.table.${label}`
    ? label
    : tableScopedLabel;
};

const EMPTY_REPORT_RESULT: IReportResult = {
  title: null,
  periodicity: ReportPeriodicity.Default,
  columns: [],
  rows: [],
  grouping: null,
};

export const normalizeReportPeriodicity = (
  periodicity?: IReportResult["periodicity"],
): ReportPeriodicity => {
  if (typeof periodicity === "number") {
    return Object.values(ReportPeriodicity).includes(periodicity)
      ? periodicity
      : ReportPeriodicity.Default;
  }

  if (typeof periodicity !== "string") {
    return ReportPeriodicity.Default;
  }

  switch (periodicity.trim().toLocaleLowerCase()) {
    case "daily":
      return ReportPeriodicity.Daily;
    case "monthly":
      return ReportPeriodicity.Monthly;
    default:
      return ReportPeriodicity.Default;
  }
};

const parseIds = (searchParams: URLSearchParams, key: string) =>
  searchParams
    .getAll(key)
    .flatMap((value) => value.split(","))
    .filter(Boolean);

const appendSearchParamValues = (
  searchParams: URLSearchParams,
  key: string,
  values?: string[],
) => {
  values?.filter(Boolean).forEach((value) => {
    searchParams.append(key, value);
  });
};

export const isSupportedReportCode = (
  reportCode: number,
): reportCode is EReportCode =>
  Object.values(EReportCode).includes(reportCode as EReportCode);

export const isValidReportDateIntervalType = (
  value: string | null | undefined,
): value is TReportDateIntervalType =>
  !!value && REPORT_DATE_INTERVAL_TYPES.includes(value as TReportDateIntervalType);

export const isValidReportMonthPeriodType = (
  value: string | null | undefined,
): value is TReportMonthPeriodType =>
  !!value && REPORT_MONTH_PERIOD_TYPES.includes(value as TReportMonthPeriodType);

export const getMonthlyPeriodRange = (date: Date) => ({
  start: startOfMonth(date),
  end: endOfMonth(date),
});

const padNumber = (value: number, length = 2) => value.toString().padStart(length, "0");

export const serializeLocalDateTime = (date?: Date | null) =>
  date
    ? `${date.getFullYear()}-${padNumber(date.getMonth() + 1)}-${padNumber(
      date.getDate(),
    )}T${padNumber(date.getHours())}:${padNumber(date.getMinutes())}:${padNumber(
      date.getSeconds(),
    )}.${padNumber(date.getMilliseconds(), 3)}`
    : undefined;

export const resolveMonthlyPeriodRange = ({
  monthPeriodType,
  selectedMonthDate,
  startDate,
  endDate,
  referenceDate = new Date(),
}: {
  monthPeriodType?: TReportMonthPeriodType;
  selectedMonthDate?: Date | null;
  startDate?: Date | null;
  endDate?: Date | null;
  referenceDate?: Date;
}) => {
  const baseDate =
    selectedMonthDate ??
    startDate ??
    endDate ??
    (monthPeriodType === "last_month"
      ? subMonths(referenceDate, 1)
      : referenceDate);

  return getMonthlyPeriodRange(baseDate);
};

export const shouldShowTicketServicesForReport = (reportCode?: EReportCode) =>
  !!reportCode &&
  [
    EReportCode.TicketsMotivoFechamentoMensal,
    EReportCode.TicketsPorInteracaoAutomacaoEnvio,
    EReportCode.TicketsPorFalhaInteracaoAutomacaoEnvio,
    EReportCode.AreasAtendimentoMenuMaisAcessados,
  ].includes(reportCode);

// Visibility and requiredness are separate so optional reports can still show this filter.
export const shouldRequireTicketServicesForReport = (
  reportCode?: EReportCode,
) =>
  !!reportCode &&
  [
    EReportCode.TicketsMotivoFechamentoMensal,
    EReportCode.TicketsPorInteracaoAutomacaoEnvio,
    EReportCode.TicketsPorFalhaInteracaoAutomacaoEnvio,
  ].includes(reportCode);

const TICKET_CHANNEL_REPORT_CODES = [
  EReportCode.TicketsPorQuadroUsuarioMensal,
  EReportCode.TicketsPorQuadroUsuarioDiario,
];

const TICKET_CHANNEL_REQUIRED_REPORT_CODES = [
  EReportCode.TicketsPorQuadroUsuarioDiario,
];

export const shouldShowTicketChannelsForReport = (reportCode?: EReportCode) =>
  !!reportCode && TICKET_CHANNEL_REPORT_CODES.includes(reportCode);

export const shouldRequireTicketChannelsForReport = (
  reportCode?: EReportCode,
) => !!reportCode && TICKET_CHANNEL_REQUIRED_REPORT_CODES.includes(reportCode);

export const shouldShowWorkspacesForReport = (reportCode?: EReportCode) =>
  !!reportCode &&
  [
    EReportCode.TicketsPorQuadroUsuarioMensal,
    EReportCode.TicketsPorQuadroUsuarioDiario,
  ].includes(reportCode);

export const shouldShowUsersForReport = (reportCode?: EReportCode) =>
  !!reportCode &&
  [
    EReportCode.TicketsPorQuadroUsuarioMensal,
    EReportCode.TicketsPorQuadroUsuarioDiario,
  ].includes(reportCode);

const MONTHLY_REPORT_CODES = [
  EReportCode.TicketsMotivoFechamentoMensal,
  EReportCode.TicketsPorQuadroUsuarioMensal,
  EReportCode.QuantidadeDeMensagensCRMPorEstabMensal,
  EReportCode.QuantidadesDeAssinaturasDigitaisPorEstabMensal,
  EReportCode.QuantidadeDeRequisicoesBarramentoDadosPorEstab,
  EReportCode.AreasAtendimentoMenuMaisAcessados,
];

export const shouldUseMonthPeriodForReport = (reportCode?: EReportCode) =>
  !!reportCode && MONTHLY_REPORT_CODES.includes(reportCode);

export const shouldShowAutomationCodeForReport = (reportCode?: EReportCode) =>
  reportCode === EReportCode.QuantidadeDeMensagensCRMPorEstabMensal;

export const shouldShowAutomationCodeExceptForReport = (
  reportCode?: EReportCode,
) => reportCode === EReportCode.QuantidadeDeMensagensCRMPorEstabMensal;

export const shouldShowEstablishmentsForReport = (reportCode?: EReportCode) =>
  !!reportCode &&
  [
    EReportCode.QuantidadeDeMensagensCRMPorEstabMensal,
    EReportCode.QuantidadesDeAssinaturasDigitaisPorEstabMensal,
    EReportCode.QuantidadeDeRequisicoesBarramentoDadosPorEstab,
  ].includes(reportCode);

export const shouldShowEsignRulesForReport = (reportCode?: EReportCode) =>
  reportCode === EReportCode.QuantidadesDeAssinaturasDigitaisPorEstabMensal;

export const parseReportExecutionSearchParams = (
  searchParams: URLSearchParams,
): IReportExecutionSearchParams => {
  const reportCode = Number(searchParams.get("reportCode"));

  return {
    reportCode: isSupportedReportCode(reportCode) ? reportCode : undefined,
    reportName: searchParams.get("reportName") ?? undefined,
    dateIntervalType: isValidReportDateIntervalType(
      searchParams.get(REPORT_DATE_INTERVAL_TYPE_QUERY_PARAM),
    )
      ? (searchParams.get(
        REPORT_DATE_INTERVAL_TYPE_QUERY_PARAM,
      ) as TReportDateIntervalType)
      : undefined,
    monthPeriodType: isValidReportMonthPeriodType(
      searchParams.get(REPORT_MONTH_PERIOD_TYPE_QUERY_PARAM),
    )
      ? (searchParams.get(
        REPORT_MONTH_PERIOD_TYPE_QUERY_PARAM,
      ) as TReportMonthPeriodType)
      : undefined,
    selectedMonthDate:
      searchParams.get(REPORT_SELECTED_MONTH_DATE_QUERY_PARAM) ?? undefined,
    startDate: searchParams.get("startDate") ?? undefined,
    endDate: searchParams.get("endDate") ?? undefined,
    workspaceId: parseIds(searchParams, "workspaceId"),
    ticketWorkspaceIds: parseIds(searchParams, "ticketWorkspaceIds"),
    ticketServiceIds: parseIds(searchParams, "ticketServiceIds"),
    ticketChannelIds: parseIds(searchParams, "ticketChannelIds"),
    userIds: parseIds(searchParams, "userIds"),
    automationCode: parseIds(searchParams, "automationCode"),
    automationCodeExcept: parseIds(searchParams, "automationCodeExcept"),
    establishmentId: parseIds(searchParams, "establishmentId"),
    esignRuleId: parseIds(searchParams, "esignRuleId"),
  };
};

export const parseReportResultSearchParams = (
  searchParams: URLSearchParams,
): IReportResultSearchParams => ({
  ...parseReportExecutionSearchParams(searchParams),
  workspaceNames: parseIds(searchParams, "workspaceNames"),
  ticketServiceNames: parseIds(searchParams, "ticketServiceNames"),
  ticketChannelNames: parseIds(searchParams, "ticketChannelNames"),
  userNames: parseIds(searchParams, "userNames"),
  automationCodeNames: parseIds(searchParams, "automationCodeNames"),
  automationCodeExceptNames: parseIds(searchParams, "automationCodeExceptNames"),
  establishmentNames: parseIds(searchParams, "establishmentNames"),
  esignRuleNames: parseIds(searchParams, "esignRuleNames"),
});

export const buildReportExecutionSearchParams = ({
  reportCode,
  reportName,
  dateIntervalType,
  monthPeriodType,
  selectedMonthDate,
  startDate,
  endDate,
  workspaceId,
  ticketWorkspaceIds,
  ticketServiceIds,
  ticketChannelIds,
  userIds,
  automationCode,
  automationCodeExcept,
  establishmentId,
  esignRuleId,
}: IReportExecutionSearchParams) => {
  const searchParams = new URLSearchParams();

  if (reportCode) {
    searchParams.set("reportCode", String(reportCode));
  }

  if (reportName) {
    searchParams.set("reportName", reportName);
  }

  if (dateIntervalType) {
    searchParams.set(REPORT_DATE_INTERVAL_TYPE_QUERY_PARAM, dateIntervalType);
  }

  if (monthPeriodType) {
    searchParams.set(REPORT_MONTH_PERIOD_TYPE_QUERY_PARAM, monthPeriodType);
  }

  if (selectedMonthDate) {
    searchParams.set(REPORT_SELECTED_MONTH_DATE_QUERY_PARAM, selectedMonthDate);
  }

  if (startDate) {
    searchParams.set("startDate", startDate);
  }

  if (endDate) {
    searchParams.set("endDate", endDate);
  }

  appendSearchParamValues(searchParams, "workspaceId", workspaceId);
  appendSearchParamValues(
    searchParams,
    "ticketWorkspaceIds",
    ticketWorkspaceIds,
  );
  appendSearchParamValues(searchParams, "ticketServiceIds", ticketServiceIds);
  appendSearchParamValues(searchParams, "ticketChannelIds", ticketChannelIds);
  appendSearchParamValues(searchParams, "userIds", userIds);
  appendSearchParamValues(searchParams, "automationCode", automationCode);
  appendSearchParamValues(
    searchParams,
    "automationCodeExcept",
    automationCodeExcept,
  );
  appendSearchParamValues(searchParams, "establishmentId", establishmentId);
  appendSearchParamValues(searchParams, "esignRuleId", esignRuleId);

  return searchParams;
};

export const buildReportResultSearchParams = ({
  workspaceNames,
  ticketServiceNames,
  ticketChannelNames,
  userNames,
  automationCodeNames,
  automationCodeExceptNames,
  establishmentNames,
  esignRuleNames,
  ...executionSearchParams
}: IReportResultSearchParams) => {
  const searchParams = buildReportExecutionSearchParams(executionSearchParams);

  appendSearchParamValues(searchParams, "workspaceNames", workspaceNames);
  appendSearchParamValues(
    searchParams,
    "ticketServiceNames",
    ticketServiceNames,
  );
  appendSearchParamValues(
    searchParams,
    "ticketChannelNames",
    ticketChannelNames,
  );
  appendSearchParamValues(searchParams, "userNames", userNames);
  appendSearchParamValues(
    searchParams,
    "automationCodeNames",
    automationCodeNames,
  );
  appendSearchParamValues(
    searchParams,
    "automationCodeExceptNames",
    automationCodeExceptNames,
  );
  appendSearchParamValues(
    searchParams,
    "establishmentNames",
    establishmentNames,
  );
  appendSearchParamValues(searchParams, "esignRuleNames", esignRuleNames);

  return searchParams;
};

export const executeReport = async (
  reportCode: EReportCode,
  params: IReportRequest,
): Promise<IReportResult> => {
  switch (reportCode) {
    case EReportCode.TicketsMotivoFechamentoMensal:
      return (await getTicketsByClosureReason(params)).data;

    case EReportCode.TicketsPorQuadroUsuarioMensal:
      return (await getTicketsByChannelAndUserMonthly(params)).data;

    case EReportCode.TicketsPorQuadroUsuarioDiario:
      return (await getTicketsByChannelAndUserDaily(params)).data;

    case EReportCode.TicketsPorInteracaoAutomacaoEnvio:
      return (await getTicketsByAutomationInteraction(params)).data;

    case EReportCode.TicketsPorFalhaInteracaoAutomacaoEnvio:
      return (await getTicketsByAutomationFailure(params)).data;

    case EReportCode.QuantidadeDeMensagensCRMPorEstabMensal:
      return (await getCrmMessagesByEstablishmentMonthly(params)).data;

    case EReportCode.QuantidadesDeAssinaturasDigitaisPorEstabMensal:
      return (await getDigitalSignaturesByEstablishmentMonthly(params)).data;

    case EReportCode.QuantidadeDeRequisicoesBarramentoDadosPorEstab:
      return (await getDataBusRequestsByEstablishment(params)).data;

    case EReportCode.AreasAtendimentoMenuMaisAcessados:
      return (await getMostAccessedTicketServiceAreasMonthly(params)).data;

    default:
      return EMPTY_REPORT_RESULT;
  }
};

const normalizeReportColumns = (columns: IReportColumn[] | null | undefined) =>
  Array.isArray(columns)
    ? columns.filter(
      (column): column is IReportColumn =>
        !!column &&
        typeof column.key === "string" &&
        typeof column.label === "string",
    )
    : [];

const normalizeReportRows = (rows: IReportRow[] | null | undefined) =>
  normalizeEmptyReportRows(
    Array.isArray(rows)
      ? rows.map((row) => ({
        isTotal: row?.isTotal === true,
        values:
          row?.values && typeof row.values === "object" ? row.values : {},
      }))
      : [],
  );

const normalizeReportGroupHeaderFields = (
  headerFields: IReportGroupHeaderField[] | null | undefined,
): IReportGroupHeaderField[] => {
  if (!Array.isArray(headerFields)) {
    return [];
  }

  return headerFields.reduce<IReportGroupHeaderField[]>((acc, field) => {
    if (!field || typeof field.key !== "string") {
      return acc;
    }

    const key = field.key.trim();

    if (!key) {
      return acc;
    }

    acc.push({
      key,
      label: typeof field.label === "string" ? field.label.trim() : null,
      format: typeof field.format === "string" ? field.format.trim() : null,
    });

    return acc;
  }, []);
};

const normalizeReportGrouping = (
  grouping?: IReportGrouping | null,
): IReportGrouping | null => {
  if (!grouping || typeof grouping.key !== "string") {
    return null;
  }

  const key = grouping.key.trim();

  if (!key) {
    return null;
  }

  return {
    key,
    label: typeof grouping.label === "string" ? grouping.label.trim() : null,
    headerFields: normalizeReportGroupHeaderFields(grouping.headerFields),
    showGroupTotals:
      typeof grouping.showGroupTotals === "boolean"
        ? grouping.showGroupTotals
        : undefined,
    showGrandTotal:
      typeof grouping.showGrandTotal === "boolean"
        ? grouping.showGrandTotal
        : undefined,
  };
};

export const normalizeReportResult = (data?: IReportResult | null): IReportResult => ({
  title: data?.title ?? null,
  periodicity: normalizeReportPeriodicity(data?.periodicity),
  columns: normalizeReportColumns(data?.columns),
  rows: normalizeReportRows(data?.rows),
  grouping: normalizeReportGrouping(data?.grouping),
});

export const createReportTableRows = (
  rows: IReportRow[],
  reportId?: string,
): IReportTableRow[] =>
  rows.map((row, index) => ({
    ...row,
    id: `${reportId ?? "report"}-${index}`,
  }));
